<script setup>
// Settings → Security: Face ID on this device and changing the PIN (docs/pwa/SPEC.md, decision 22).
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { extractApiError } from '../apiError';
import { locale, t } from '../i18n';
import {
  biometricWords, isCancelled, isWebAuthnSupported, registerPasskey, storedPasskeyId, storePasskeyId,
} from '../webauthn';
import SecurityDialog from './SecurityDialog.vue';

// rows: list rows inside a «Профиль» group on a phone (docs/design/SPEC.md, «Профиль»).
defineProps({
  rows: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['message', 'error']);

const loaded = ref(false);
const passwordConfirmed = ref(false);
const busy = ref(false);
const dialog = ref(null);
const thisDeviceId = ref(storedPasskeyId());

const supported = isWebAuthnSupported();
const method = computed(() => biometricWords(locale.value));
const faceIdOn = computed(() => thisDeviceId.value !== null);

async function load() {
  try {
    const [security, list] = await Promise.all([
      axios.get('/user/security'),
      axios.get('/user/passkeys'),
    ]);
    passwordConfirmed.value = security.data.password_confirmed;
    // The key of this device is gone on the server: Face ID is off here.
    if (thisDeviceId.value && !list.data.passkeys.some((p) => p.id === thisDeviceId.value)) {
      storePasskeyId(null);
      thisDeviceId.value = null;
    }
  } catch (e) {
    if (e?.response?.status !== 401 && e?.response?.status !== 423) {
      emit('error', extractApiError(e, t('settings.loadFailed')));
    }
  } finally {
    loaded.value = true;
  }
}

// No password step: the app is already unlocked (docs/pwa/SPEC.md, decision 24).
async function enableFaceId() {
  busy.value = true;
  try {
    const data = await registerPasskey();
    thisDeviceId.value = data.credential_id;
    emit('message', t('security.passkeyOn', method.value));
  } catch (e) {
    if (e?.response?.data?.reason === 'password_confirmation_required') {
      passwordConfirmed.value = false;
      dialog.value = 'add-passkey';
    } else if (!isCancelled(e)) {
      emit('error', extractApiError(e, t('security.passkeyAddFailed')));
    }
  } finally {
    busy.value = false;
  }
}

async function disableFaceId() {
  busy.value = true;
  try {
    await axios.delete(`/user/passkeys/${encodeURIComponent(thisDeviceId.value)}`);
  } catch (e) {
    // 404: already removed on the server — Face ID is off here either way.
    if (e?.response?.status !== 404) {
      emit('error', extractApiError(e, t('security.failed')));
      busy.value = false;
      return;
    }
  }
  storePasskeyId(null);
  thisDeviceId.value = null;
  emit('message', t('security.passkeyOff', method.value));
  busy.value = false;
}

function toggleFaceId() {
  if (busy.value) {
    return;
  }
  if (faceIdOn.value) {
    disableFaceId();
  } else {
    enableFaceId();
  }
}

function onConfirmed() {
  dialog.value = null;
  passwordConfirmed.value = true;
  enableFaceId();
}

function onPinSaved(text) {
  dialog.value = null;
  passwordConfirmed.value = true;
  emit('message', text);
}

onMounted(load);
</script>

<template>
  <div v-if="rows">
    <div class="flex min-h-12 items-center justify-between gap-3 border-t border-slate-800 px-5 py-2">
      <span class="min-w-0">
        <span
          id="faceid-row-label"
          class="block text-[0.9375rem] text-slate-100"
        >{{ t('security.faceIdOnDevice', method) }}</span>
        <span
          v-if="!supported"
          class="block text-[0.8125rem] text-slate-500"
        >{{ t('security.unsupported') }}</span>
      </span>
      <button
        v-if="supported"
        type="button"
        role="switch"
        :aria-checked="faceIdOn"
        aria-labelledby="faceid-row-label"
        class="relative inline-flex h-[31px] w-[51px] shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
        :class="faceIdOn ? 'bg-emerald-600' : 'bg-slate-700'"
        :disabled="busy || !loaded"
        @click="toggleFaceId"
      >
        <span
          class="inline-block size-[27px] rounded-full bg-white shadow transition-transform"
          :class="faceIdOn ? 'translate-x-[22px]' : 'translate-x-0.5'"
        />
      </button>
    </div>
    <button
      type="button"
      class="flex min-h-12 w-full items-center justify-between gap-3 border-t border-slate-800 px-5 text-left text-[0.9375rem] text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800 disabled:opacity-50"
      :disabled="busy"
      @click="dialog = 'set-pin'"
    >
      {{ t('security.changePin') }}
      <span
        class="text-slate-500"
        aria-hidden="true"
      >›</span>
    </button>
    <SecurityDialog
      v-if="dialog"
      :purpose="dialog"
      :password-confirmed="passwordConfirmed"
      @close="dialog = null"
      @confirmed="onConfirmed"
      @saved="onPinSaved"
    />
  </div>
  <section
    v-else
    class="rounded-lg border border-slate-700 bg-slate-900/40 p-5 lg:col-span-2"
  >
    <h3 class="text-sm font-medium text-slate-300">
      {{ t('security.title') }}
    </h3>
    <p class="mt-1 text-xs text-slate-500">
      {{ t('security.hint') }}
    </p>

    <p
      v-if="!loaded"
      class="mt-4 text-sm text-slate-400"
    >
      {{ t('common.loading') }}
    </p>

    <template v-else>
      <div class="mt-5 flex items-center justify-between gap-4">
        <div class="min-w-0">
          <p
            id="faceid-label"
            class="text-sm font-medium text-slate-200"
          >
            {{ t('security.faceIdOnDevice', method) }}
          </p>
          <p class="mt-0.5 text-xs text-slate-500">
            {{ supported ? t('security.faceIdHint', method) : t('security.unsupported') }}
          </p>
        </div>
        <button
          v-if="supported"
          type="button"
          role="switch"
          :aria-checked="faceIdOn"
          aria-labelledby="faceid-label"
          class="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
          :class="faceIdOn ? 'bg-emerald-600' : 'bg-slate-700'"
          :disabled="busy"
          @click="toggleFaceId"
        >
          <span
            class="inline-block size-6 rounded-full bg-white shadow transition-transform"
            :class="faceIdOn ? 'translate-x-[22px]' : 'translate-x-0.5'"
          />
        </button>
      </div>

      <div class="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-sm font-medium text-slate-200">
            {{ t('security.pin') }}
          </p>
          <p class="mt-0.5 text-xs text-slate-500">
            {{ t('security.pinHint') }}
          </p>
        </div>
        <button
          type="button"
          class="rounded bg-slate-800 px-4 py-2 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50"
          :disabled="busy"
          @click="dialog = 'set-pin'"
        >
          {{ t('security.changePin') }}
        </button>
      </div>
    </template>

    <SecurityDialog
      v-if="dialog"
      :purpose="dialog"
      :password-confirmed="passwordConfirmed"
      @close="dialog = null"
      @confirmed="onConfirmed"
      @saved="onPinSaved"
    />
  </section>
</template>
